import {
  IndividualAccountTypeEnum,
  InstitutionAccountTypeEnum,
  JointAccountTypeEnum,
} from "../../graphql/supergraphEnums";
import {
  type AccountTypeEnum,
  ComplianceIndividualProfileInput,
  ComplianceInstitutionProfileInput,
  ComplianceJointProfileInput,
  type ProfileType,
} from "../../graphql/supergraphTypes";
import {
  getAccountTypeCategory,
  isIndividualAccountType,
  isJoinAccountType,
} from "../../utils/account_types";
import {
  buildJointDomicileAddresses,
  getSafeDate,
  profileAddressesToAddresses,
} from "../common/ProfileManagement/profile_utils";
import { DeclarationProfileType } from "../profile/ProfileDeclaration/types";
import { AccountFormValues } from "./types";

export const prepareIndividualPayload = (
  caseId: number,
  values: AccountFormValues,
  profile?: DeclarationProfileType | null,
): ComplianceIndividualProfileInput => {
  return {
    caseId: caseId,
    accountType: values.accountType as IndividualAccountTypeEnum,
    firstName: profile?.firstName || "",
    middleName: profile?.middleName,
    lastName: profile?.lastName || "",
    dateOfBirth: getSafeDate(profile?.dateOfBirth),
    taxId: profile?.taxIdUnencrypted,
    taxIdType: profile?.taxIdType,
    addresses: profileAddressesToAddresses(profile?.addresses),
  };
};

export const prepareInstitutionPayload = (
  caseId: number,
  values: AccountFormValues,
  profile?: DeclarationProfileType | null,
): ComplianceInstitutionProfileInput => {
  const defaultAmlEntityId =
    isJoinAccountType(values.accountType) || isIndividualAccountType(values.accountType)
      ? 1
      : -1;
  return {
    caseId: caseId,
    accountType: values.accountType as InstitutionAccountTypeEnum,
    legalBusinessName: profile?.legalName || "",
    dateOfFormation: getSafeDate(profile?.dateOfOrganization),
    taxId: profile?.taxIdUnencrypted,
    taxIdType: profile?.taxIdType,
    amlEntityId: defaultAmlEntityId,
    addresses: profileAddressesToAddresses(profile?.addresses),
  };
};

export const prepareJointPayload = (
  caseId: number,
  values: AccountFormValues,
  profile?: DeclarationProfileType | null,
): ComplianceJointProfileInput => {
  return {
    caseId: caseId,
    accountType: values.accountType as JointAccountTypeEnum,
    legalBusinessName: profile?.legalName || "",
    amlEntityId: 1,
    addresses: buildJointDomicileAddresses(profile?.addresses?.[0]?.country?.alpha2code),
  };
};

export const getAllAffectedDescendantProfiles = (
  allProfiles: ProfileType[],
  parentProfileId: string | undefined,
): ProfileType[] => {
  if (!allProfiles?.length || !parentProfileId) return [];

  const targetParentId = Number(parentProfileId);

  //  Build an Adjacency List (Map) for O(1) child lookups
  const childrenMap = new Map<number, ProfileType[]>();

  for (const profile of allProfiles) {
    if (!profile.parentRelationships) continue;

    for (const rel of profile.parentRelationships) {
      if (rel?.parentProfileId !== undefined) {
        let children = childrenMap.get(rel.parentProfileId);
        if (!children) {
          children = [];
          childrenMap.set(rel.parentProfileId, children);
        }
        children.push(profile);
      }
    }
  }

  // Traverse the hierarchy using the pre-computed map
  const collectedProfiles: ProfileType[] = [];
  const visitedIds = new Set<number>();
  const stack = [targetParentId];

  while (stack.length > 0) {
    const currentParentId = stack.pop()!;
    const children = childrenMap.get(currentParentId) || [];

    for (const child of children) {
      if (!visitedIds.has(child.id)) {
        visitedIds.add(child.id);
        stack.push(child.id);

        collectedProfiles.push(child);
      }
    }
  }

  return collectedProfiles.sort((a, b) => (a.path ?? "").localeCompare(b.path ?? ""));
};

/**
 * Check if switching account types requires showing warning modal
 * Shows warning for any category change (individual/joint/entity)
 */
export const isCategoryChanged = (
  currentAccountType: AccountTypeEnum | undefined,
  newAccountType: AccountTypeEnum,
): boolean => {
  if (!currentAccountType) return false;

  const currentCategory = getAccountTypeCategory(currentAccountType);
  const newCategory = getAccountTypeCategory(newAccountType);

  return currentCategory !== newCategory;
};
